import { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { purchaseReportingApi } from '../../services/purchaseReportingApi';
import type { PRFilters, PRDetailRow, PaginationInfo } from '../../types/purchaseReporting';

interface Props {
    filters: PRFilters;
    refreshKey: number;
}

const PAGE_SIZE = 50;

function formatNumber(n: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

function formatDate(iso: string): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB');
}

export function DetailTab({ filters, refreshKey }: Props) {
    const [items, setItems] = useState<PRDetailRow[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState('postingDate');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');

    useEffect(() => { setPage(1); }, [filters, search, refreshKey]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        purchaseReportingApi.getDetail({
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            company: filters.company || undefined,
            businessUnit: filters.businessUnit || undefined,
            vendorID: filters.vendorID || undefined,
            locationID: filters.locationID || undefined,
            search: search || undefined,
            sortBy,
            sortOrder,
            page,
            limit: PAGE_SIZE,
        })
            .then(res => {
                setItems(res.items);
                setPagination(res.pagination);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [filters, sortBy, sortOrder, page, search, refreshKey]);

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(field);
            setSortOrder('DESC');
        }
        setPage(1);
    };

    const sortIcon = (field: string) => {
        if (sortBy !== field) return '';
        return sortOrder === 'ASC' ? ' \u2191' : ' \u2193';
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    if (error) return <div className="text-red-600 p-4">{error}</div>;

    const columns = [
        { key: 'companyCode', label: 'Co.' },
        { key: 'postingDate', label: 'Date' },
        { key: 'productCode', label: 'Product' },
        { key: 'productName', label: 'Name' },
        { key: 'vendorCode', label: 'Vendor' },
        { key: 'vendorName', label: 'Vendor Name' },
        { key: 'warehouseName', label: 'Warehouse' },
        { key: 'quantity', label: 'Qty' },
        { key: 'costLCY', label: 'Cost (LCY)' },
    ];

    return (
        <div className="space-y-4">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        placeholder="Search product or vendor..."
                        className="w-full pl-10 pr-3 py-1.5 border rounded-md text-sm"
                    />
                </div>
                <button type="submit" className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    Search
                </button>
                {search && (
                    <button
                        type="button"
                        onClick={() => { setSearch(''); setSearchInput(''); }}
                        className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
                    >
                        Clear
                    </button>
                )}
            </form>

            {loading && items.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-gray-50">
                                {columns.map(col => (
                                    <th
                                        key={col.key}
                                        onClick={() => handleSort(col.key)}
                                        className="px-3 py-2 text-left font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                                    >
                                        {col.label}{sortIcon(col.key)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => (
                                <tr key={i} className="border-b hover:bg-gray-50">
                                    <td className="px-3 py-2 text-gray-500">{row.companyCode}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.postingDate)}</td>
                                    <td className="px-3 py-2 text-gray-500">{row.productCode}</td>
                                    <td className="px-3 py-2">{row.productName}</td>
                                    <td className="px-3 py-2 text-gray-500">{row.vendorCode}</td>
                                    <td className="px-3 py-2">{row.vendorName}</td>
                                    <td className="px-3 py-2">{row.warehouseName}</td>
                                    <td className="px-3 py-2 text-right font-mono">{formatNumber(row.quantity)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{formatNumber(row.costLCY)}</td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">No data for selected filters</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>
                        {formatNumber(pagination.total)} records | Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <div className="flex gap-2">
                        <button onClick={() => setPage(p => p - 1)} disabled={!pagination.hasPrev} className="px-3 py-1 border rounded disabled:opacity-40">Previous</button>
                        <button onClick={() => setPage(p => p + 1)} disabled={!pagination.hasNext} className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
